/**
 * Lightweight internal dev-alert helper.
 *
 * Fire-and-forget email notification for when the app detects it silently
 * dropped/excluded data it probably shouldn't have (e.g. a failed Reference
 * Price match, a holder missing required data) -- not user-facing, purely so
 * a real data gap surfaces as an email instead of vanishing with no signal.
 * This is a stopgap, not a fix: it doesn't change the underlying drop
 * behavior, it just makes sure someone finds out about it.
 *
 * Uses the same /portal/auth/send-email endpoint as every other transactional
 * email in this app (see send-messages.js, ledgers.js, investor-agreement.js,
 * etc.), reading client/userid/cookie from the ambient globals set at page
 * load -- matching the convention used at every other call site for this
 * endpoint. Failures are swallowed (console.error only): an alert that fails
 * to send should never break the calling feature.
 */

const DEV_ALERT_RECIPIENT = 'cmcconnell@nthround.com';

// 2026-08-05 fix (per Chris -- Requests admin was emailing the same "27
// unmatched" alert on every single page load/refresh): this helper never had
// any dedup. A condition that doesn't go away (e.g. legacy transfer_requests
// that can never find a matching modern Transaction) re-sent an identical
// email every time ANY call site's detection logic ran, with no cooldown.
// Fingerprint subject+client+message and skip resending an unchanged alert
// within DEV_ALERT_COOLDOWN_MS; a genuinely NEW or DIFFERENT alert (content
// hash changes) still sends immediately regardless of cooldown. Scoped by
// client so one client's noisy alert can't suppress another's. This is
// per-browser (localStorage), not a true cross-user/server-side dedup -- it
// stops the same person re-triggering the same alert by reloading/revisiting
// the page repeatedly, which is the common case, but two different admins
// hitting the same page for the first time within the cooldown window will
// still each trigger one email.
const DEV_ALERT_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const DEV_ALERT_STORAGE_PREFIX = 'nth_dev_alert_last_sent:';

// Bulk operations (e.g. the JMS Payment Scanner in edit.js, which calls
// getPayments() -- and therefore this alert -- once per holder across the
// whole roster) can legitimately trigger this same "real data gap" condition
// dozens of times within seconds, once per holder, each with different
// content. The dedup above only suppresses an *unchanged* repeat, so it does
// nothing to stop that -- every holder's alert body differs (different
// nrids/share prices), so each reads as new and sends. suppressDevAlerts() /
// restoreDevAlerts() give a call site an explicit, opt-in way to silence real
// sends for the duration of a bulk pass. Default is unsuppressed, so every
// existing call site (allocator.js, payments.js, requests.js, enroll.js,
// verify-signed-doc.js, shared_assets/index.js) behaves exactly as before
// unless it explicitly opts in.
// 2026-09-06: suppressed session-wide per Chris -- normal (non-scan) page loads
// were each firing a real, unsuppressed alert email while investigating post-2010
// vintage gaps. Flip back to false once that investigation settles down.
let devAlertsSuppressed = true;

export function suppressDevAlerts() {
	devAlertsSuppressed = true;
}

export function restoreDevAlerts() {
	devAlertsSuppressed = false;
}

function hashDevAlertContent(str) {
	// Not cryptographic -- just a cheap, stable fingerprint (DJB2-style) to tell
	// "same alert content as last time" from "something actually changed".
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = (hash << 5) - hash + str.charCodeAt(i);
		hash |= 0; // force 32-bit int
	}
	return hash;
}

function shouldSuppressDevAlert(storageKey, contentHash) {
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) return false;
		const last = JSON.parse(raw);
		if (!last || last.hash !== contentHash) return false; // content changed -- never suppress a new/different alert
		return Date.now() - Number(last.sentAt || 0) < DEV_ALERT_COOLDOWN_MS;
	} catch (err) {
		// Never let a localStorage/JSON hiccup block a real alert from sending.
		return false;
	}
}

function recordDevAlertSent(storageKey, contentHash) {
	try {
		localStorage.setItem(storageKey, JSON.stringify({ hash: contentHash, sentAt: Date.now() }));
	} catch (err) {
		// Non-fatal -- worst case this alert can re-send before its cooldown.
	}
}

/**
 * @param {string} subject - short description of what was dropped
 * @param {string} message - HTML body; details of what was excluded and why
 */
export function sendDevAlert(subject, message) {
	try {
		// 2026-09-07: JM Search alerts killed outright per Chris -- this client hit a
		// flood of real (correctly unsuppressed) alerts from a known-unstable area
		// (post-2010 vintage/assumption gaps). Hard-blocked regardless of
		// devAlertsSuppressed/suppressDevAlerts() state; other clients unaffected.
		const alertClient = typeof client !== 'undefined' ? client : '';
		if (alertClient === 'jmsearch') return;
		if (devAlertsSuppressed) return;

		const storageKey = `${DEV_ALERT_STORAGE_PREFIX}${alertClient}:${subject}`;
		const contentHash = hashDevAlertContent(message);

		if (shouldSuppressDevAlert(storageKey, contentHash)) return;

		axios
			.post(
				'https://api.nthround.io/portal/auth/send-email',
				JSON.stringify({
					client: alertClient,
					userid: typeof authedUser !== 'undefined' ? authedUser : '',
					cookie: typeof cookie !== 'undefined' ? cookie : '',
					sender_name: 'Nth Round System Alert',
					recipient: DEV_ALERT_RECIPIENT,
					subject: `[Dev Alert] ${subject}`,
					message,
				})
			)
			.then(() => recordDevAlertSent(storageKey, contentHash))
			.catch((err) => console.error('sendDevAlert failed to send:', err));
	} catch (err) {
		console.error('sendDevAlert failed:', err);
	}
}
